import { Button, Box, Typography, Stack, Checkbox, Divider, Card, CardContent, Alert, Accordion, AccordionSummary, AccordionDetails, } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";

// Reusable styled box for label-value pairs
const FieldBox = ({ label, value, flex = 1, minWidth = 0 }) => (
    <Box sx={{ flex, minWidth, p: 1, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>{label}</Typography>
        <Typography variant="body2" fontWeight={500}>{value || "—"}</Typography>
    </Box>
);

export default function HRReviewSubmit({ formData, documents, pendingFiles, setActiveStep }) {

    // ✅ HELPER FUNCTION TO FORMAT DATE AS DD/MM/YYYY
    const formatDate = (date) => {
        if (!date) return "";
        return dayjs(date).format("DD/MM/YYYY");
    };

    return (
        <>
            <Stack spacing={3}>
                <Alert severity="info">Review all details before submitting</Alert>

                {/* PERSONAL DETAILS */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Personal Details</Typography>
                            <Button size="small" onClick={() => setActiveStep(0)}>Edit</Button>
                        </Box>
                        <Divider sx={{ my: 1 }} />

                        <Stack spacing={1.5}>
                            {/* ROW 1: Name + Email */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <FieldBox label="Name" value={formData.name} minWidth={280} />
                                <FieldBox label="Email ID" value={formData.emailId} minWidth={280} />
                            </Box>

                            {/* ROW 2: Mobile + DOB + Gender */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                                    <FieldBox label="Mobile No." value={formData.mobileNo} />
                                    <FieldBox label="Date of Birth" value={formatDate(formData.dob)} />
                                </Box>
                                <FieldBox label="Gender" value={formData.gender} minWidth={280} />
                            </Box>

                            {/* ROW 3: Address + PIN/City/State/Country */}
                            <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                                <Box sx={{ display: "flex", flex: 1 }}>
                                    <FieldBox label="Address" value={formData.address} />
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: 'column', flex: 1, gap: 1 }}>
                                    <Box sx={{ display: "flex", flex: 1, gap: 1 }}>
                                        <FieldBox label="PIN Code" value={formData.pinCode} flex={0.4} />
                                        <FieldBox label="City" value={formData.city || ""} flex={0.6} />
                                    </Box>
                                    <Box display="flex" flex={1} gap={1}>
                                        <FieldBox label="State" value={formData.state || ""} flex={0.6} />
                                        <FieldBox label="Country" value={formData.country || ""} flex={0.4} />
                                    </Box>
                                </Box>
                            </Box>

                            {/* ROW 4: Apply For + Date of Application + Native Place + Alt Mobile */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Box display="flex" flex={1} gap={1}>
                                    <FieldBox label="Apply For" value={formData.applyFor} flex={0.6} />
                                    <FieldBox label="Date of Application" value={formatDate(formData.dateOfApplication)} flex={0.4} />
                                </Box>
                                <Box display="flex" flex={1} gap={1}>
                                    <FieldBox label="Native Place" value={formData.nativePlace} flex={0.6} />
                                    <FieldBox label="Alternate Mobile No." value={formData.altMobileNo || "—"} flex={0.4} />
                                </Box>
                            </Box>

                            {/* ROW 5: Marital Status + Blood Group */}
                            <Box display="flex" flex={1} gap={1}>
                                <FieldBox label="Marital Status" value={formData.maritalStatus} />
                                <FieldBox label="Blood Group" value={formData.bloodGroup || "—"} />
                            </Box>

                            {/* ROW 6: Spouse (if married) */}
                            {formData.maritalStatus === "Married" && (
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <FieldBox label="Spouse Name" value={formData.spouseName} minWidth={280} />
                                    <FieldBox label="Spouse Contact No." value={formData.spouseContact} minWidth={280} />
                                </Box>
                            )}

                            {/* ROW 7: Father */}
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <FieldBox label="Father Name" value={formData.fatherName || "—"} minWidth={280} />
                                <FieldBox label="Father Contact No." value={formData.fatherContact || "—"} minWidth={280} />
                            </Box>

                            {/* ROW 8: Mother */}
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <FieldBox label="Mother Name" value={formData.motherName || "—"} minWidth={280} />
                                <FieldBox label="Mother Contact No." value={formData.motherContact || "—"} minWidth={280} />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {/* EDUCATION */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Education Details</Typography>
                            <Button size="small" onClick={() => setActiveStep(1)}>Edit</Button>
                        </Box>
                        <Divider sx={{ my: 1 }} />

                        <Stack spacing={2}>
                            {formData.educationDetails && formData.educationDetails.map((edu, index) => (
                                <Accordion key={index} defaultExpanded={index === 0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography fontWeight={"bold"}>Education {index + 1}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Stack spacing={1.5}>
                                            {/* ROW 1: Qualification + College */}
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <FieldBox label="Qualification" value={edu.qualification} />
                                                <FieldBox label="College Name" value={edu.collegeName} />
                                            </Box>

                                            {/* ROW 2: Passing Year + Percentage */}
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <FieldBox label="Passing Year" value={edu.passingYear} />
                                                <FieldBox label="Percentage / CGPA" value={edu.percentage} />
                                            </Box>
                                        </Stack>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                {/* EXPERIENCE */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Experience</Typography>
                            <Button size="small" onClick={() => setActiveStep(2)}>Edit</Button>
                        </Box>
                        <Divider sx={{ my: 1 }} />

                        <Stack spacing={2}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <FieldBox label="Work Experience?" value={formData.hasExperience} />
                            </Box>

                            {formData.hasExperience === "Yes" && (
                                <>
                                    {/* Total Experience */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <FieldBox label="Years of Experience" value={`${formData.experienceYears} year${formData.experienceYears !== 1 ? 's' : ''}`} />
                                        <FieldBox label="Months" value={`${formData.experienceMonths} month${formData.experienceMonths !== 1 ? 's' : ''}`} />
                                    </Box>

                                    {formData.experienceDetails && formData.experienceDetails.map((exp, index) => (
                                        <Accordion key={index} defaultExpanded={index === 0}>
                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                <Typography fontWeight={"bold"}>Experience {index + 1}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack spacing={1.5}>
                                                    {/* ROW 1: Company Name + Job Title */}
                                                    <Box sx={{ display: "flex", gap: 1 }}>
                                                        <FieldBox label="Company Name" value={exp.companyName} />
                                                        <FieldBox label="Job Title" value={exp.jobTitle} />
                                                    </Box>

                                                    {/* ROW 2: Salary + From Date + To Date + Currently Working */}
                                                    <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
                                                        <FieldBox label="Salary (₹/month)" value={exp.salaryPerMonth ? `₹${exp.salaryPerMonth}` : "—"} />
                                                        <FieldBox label="From" value={formatDate(exp.fromDate)} />
                                                        <FieldBox label="To Date" value={exp.currentlyWorking ? "Currently Working" : formatDate(exp.toDate)} />
                                                        <Box sx={{ flex: 1, p: 1, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center' }}>
                                                            <Checkbox checked={exp.currentlyWorking} disabled size="small" />
                                                            <Typography variant="body2">Currently Working</Typography>
                                                        </Box>
                                                    </Box>

                                                    {/* ROW 3: Reason for Leaving */}
                                                    <FieldBox label="Reason for Leaving" value={exp.reasonForLeaving} />
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                {/* DOCUMENTS & PREFERENCE */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Documents & Preferences</Typography>
                            <Button size="small" onClick={() => setActiveStep(3)}>Edit</Button>
                        </Box>
                        <Divider sx={{ my: 1 }} />

                        <Stack spacing={1.5}>
                            {/* ROW 1: English Proficiency + Languages Known */}
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <FieldBox label="English Proficiency" value={formData.englishProficiency || "—"} />
                                <FieldBox label="Languages Known" value={formData.languagesKnown || "—"} />
                            </Box>

                            {/* ROW 2: Salary Expected + Notice Period + Joining Date */}
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <FieldBox label="Salary Expected" value={formData.salaryExpected ? `₹${formData.salaryExpected}` : "—"} />
                                <FieldBox label="Notice Period" value={formData.noticePeriod || "—"} />
                                <FieldBox label="Preferred Joining Date" value={formatDate(formData.joiningDate) || "—"} />
                            </Box>

                            {/* ROW 3: Documents */}
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <FieldBox label="Resume" value={pendingFiles.Resume ? pendingFiles.Resume.name : documents.find(d => d.DocumentType === "Resume") ? "Uploaded" : "Not uploaded"} />
                                <FieldBox label="Photo" value={pendingFiles.Photo ? pendingFiles.Photo.name : documents.find(d => d.DocumentType === "Photo") ? "Uploaded" : "Not uploaded"} />
                                <FieldBox label="ID Proof" value={pendingFiles.IdProof ? pendingFiles.IdProof.name : documents.find(d => d.DocumentType === "IdProof") ? "Uploaded" : "Not uploaded"} />
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </Stack>
        </>
    );
}